import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import streamApi from '../../../services/streamApi';
import { useAdmin } from '../AdminContext';

function formatTimestamp(value) {
  if (!value) return 'Unknown';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function ModerationPage() {
  const {
    pendingMedia,
    pendingError,
    setPendingError,
    setNotice,
    notice,
    removePendingMedia,
  } = useAdmin();
  const { refreshing, loadDashboard } = useOutletContext();

  const [actingMediaId, setActingMediaId] = useState('');

  async function handleModeration(mediaId, action) {
    setActingMediaId(mediaId);
    setPendingError('');
    setNotice('');

    try {
      if (action === 'approve') {
        await streamApi.approveMedia(mediaId);
        setNotice('Media approved successfully.');
      } else {
        await streamApi.rejectMedia(mediaId);
        setNotice('Media rejected successfully.');
      }

      removePendingMedia(mediaId);
    } catch (actionError) {
      setPendingError(actionError.message || `Failed to ${action} media.`);
    } finally {
      setActingMediaId('');
    }
  }

  return (
    <>
      <header className="admin-dashboard__header">
        <div>
          <h1 className="admin-dashboard__title">Moderation</h1>
          <p className="admin-dashboard__subtitle">
            Review uploaded media before it becomes available for streamer submission.
          </p>
        </div>
        <button
          type="button"
          className="admin-dashboard__refresh"
          onClick={() => loadDashboard({ silent: true })}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing...' : 'Refresh Queue'}
        </button>
      </header>

      {notice && <div className="admin-dashboard__msg admin-dashboard__msg--success">{notice}</div>}

      <div className="admin-dashboard__card">
        {pendingError ? (
          <div className="admin-dashboard__empty">{pendingError}</div>
        ) : pendingMedia.length === 0 ? (
          <div className="admin-dashboard__empty">No pending media to review right now.</div>
        ) : (
          <div className="admin-dashboard__media-grid">
            {pendingMedia.map((item) => {
              const previewUrl = streamApi.resolveImageUrl(
                item.image_preview_url || item.image_url || item.image_key
              );
              const ownerId = item.uploader_username || item.uploader || item.owner_id || item.owner_user_id || 'Unknown';
              const mediaType = item.media_type || 'image';
              const isActing = actingMediaId === item.id;

              return (
                <article key={item.id} className="admin-dashboard__media-card">
                  <div className="admin-dashboard__media-thumb">
                    {previewUrl ? (
                      <img src={previewUrl} alt={item.title || 'Uploaded media'} />
                    ) : (
                      <div className="admin-dashboard__media-no-image">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <polyline points="21 15 16 10 5 21" />
                        </svg>
                      </div>
                    )}
                    <span className="admin-dashboard__media-status admin-dashboard__media-status--pending">
                      pending
                    </span>
                  </div>

                  <div className="admin-dashboard__media-body">
                    <p className="admin-dashboard__media-title">{item.title || 'Untitled media'}</p>
                    <p className="admin-dashboard__media-uploader">by {ownerId}</p>
                    <dl className="admin-dashboard__meta-list">
                      <div>
                        <dt>Type</dt>
                        <dd>{mediaType}</dd>
                      </div>
                      <div>
                        <dt>Submitted</dt>
                        <dd>{formatTimestamp(item.created_at)}</dd>
                      </div>
                    </dl>

                    <div className="admin-dashboard__media-actions">
                      <button
                        type="button"
                        className="admin-dashboard__action-btn admin-dashboard__action-btn--approve"
                        onClick={() => handleModeration(item.id, 'approve')}
                        disabled={isActing}
                      >
                        {isActing ? 'Working...' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        className="admin-dashboard__action-btn admin-dashboard__action-btn--reject"
                        onClick={() => handleModeration(item.id, 'reject')}
                        disabled={isActing}
                      >
                        {isActing ? 'Working...' : 'Reject'}
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

export default ModerationPage;
